import { getAuthHeaders } from '@/utils/getauthheaders';
import { fetchTransportDetails } from '@/types/shipments';

const API_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;
const STORAGE_KEY = 'transports_cache';
const CACHE_DURATION = 600 * 1000; // 10 minute

class TransportService {
  // Helper function to extract cargo information from description
  extractCargoInfo(descriere) {
    if (!descriere) return { weight: null, distance: null, cargoType: null };
    
    // Extract weight (tone, kg)
    const weightMatch = descriere.match(/(\d+(?:\.\d+)?)\s*(tone?|kg|t\b)/i);
    const weight = weightMatch ? `${weightMatch[1]} ${weightMatch[2].toLowerCase()}` : null;
    
    // Extract distance (km)
    const distanceMatch = descriere.match(/(\d+(?:\.\d+)?)\s*km/i);
    const distance = distanceMatch ? `${distanceMatch[1]} km` : null;
    
    // Extract cargo type (first part before dash or comma)
    const cargoType = descriere.split(/[-,]/)[0]?.trim() || null;
    
    return { weight, distance, cargoType };
  }

  // Transform API data to application format
  transformTransportData(data) {
    return data.data?.map(transport => {
      const descriere = transport.descriere || '';
      const cargoInfo = this.extractCargoInfo(descriere);

      // Determine the actual departure location
      const actualDepartureLocation = transport.plecare_oras || transport.client_city || 'Plecare necunoscută';
      const actualDepartureAddress = transport.plecare_adresa || transport.client_address || '';

      return {
        id: transport.id,
        client: transport.client_nume || transport.client || 'Client necunoscut',
        client_id: transport.client_id,
        route: transport.ruta,
        // Use the actual departure location (client or separate)
        startLocation: actualDepartureLocation,
        endLocation: transport.destinatie_oras || 'Nu se poate lua adresa',
        status: transport.status,
        prioritate: transport.prioritate || 'normala',
        progres: parseInt((transport.progres?.toString().replace('%', '')) || '0', 10),
        date: transport.data_transport ? transport.data_transport.split('T')[0] : new Date().toISOString().split('T')[0],
        ora_plecarii: transport.ora_plecarii,
        ora_estimata: transport.ora_estimata,
        value: parseFloat((transport.valoare?.replace(/[^\d.-]/g, '') || transport.value || 0)),
        tip_plata: transport.tip_plata || 'cash',
        description: transport.descriere || '',
        weight: cargoInfo.weight || null,
        vehicle: transport.vehicul_complet ||
                 `${transport.vehicul_marca || ''} ${transport.vehicul_model || ''} - ${transport.vehicul_numar || ''}`.trim() ||
                 'Vehicul necunoscut',
        driver: transport.sofer_nume || 'Șofer necunoscut',
        sofer_id: transport.sofer_id,
        vehicul_id: transport.vehicul_id,
        locatie_plecare_id: transport.locatie_plecare_id,
        locatie_destinatie_id: transport.locatie_destinatie_id,
        created_at: transport.created_at,
        updated_at: transport.updated_at,
        
        // Store both departure addresses for reference
        plecare_adresa: actualDepartureAddress,
        client_adresa: transport.client_address,
        
        // Vehicle location data
        vehicul_adresa: transport.vehicul_adresa || null,
        vehicul_lat: transport.vehicul_lat || null,
        vehicul_lng: transport.vehicul_lng || null,
        vehicul_viteza: transport.vehicul_viteza || null,
        vehicul_ultima_pozitie: transport.vehicul_ultima_pozitie || null,
        
        // IMPORTANT: Include tracking_token from backend
        tracking_token: transport.tracking_token || null
      };
    }) || [];
  }

  // Fetch transports with caching
  async fetchTransports() {
    try {
      // Check cache first
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const isValid = parsed.timestamp && (Date.now() - parsed.timestamp < CACHE_DURATION);
        if (isValid) {
          return parsed.data;
        }
      }

      const response = await fetch(`${API_BASE_URL}/shipments?format=detailed`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Eroare la încărcarea transporturilor');
      }

      const data = await response.json();
      const transformedData = this.transformTransportData(data);

      // Save to localStorage with timestamp
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        timestamp: Date.now(),
        data: transformedData
      }));

      return transformedData;
    } catch (error) {
      console.error('Eroare la încărcarea transporturilor:', error);
      throw error;
    }
  }

  // Fetch detailed transport information
  // Fetch detailed transport information
async fetchTransportDetails(transportId) {
  try {
    const detailedTransport = await fetchTransportDetails(transportId);
    console.log('Detalii transport:', detailedTransport);
    
    // Extract cargo info from description if available
    const description = detailedTransport.data?.cargo_details?.descriere_completa || 
                       detailedTransport.data?.descriere?.descriere_completa || '';
    const cargoInfo = this.extractCargoInfo(description);
    
    return {
      id: detailedTransport.data?.transport?.id,
      
      // Client information
      client: detailedTransport.data?.client_details?.nume_firma,
      client_id: detailedTransport.data?.client_details?.id,
      telefon: detailedTransport.data?.client_details?.telefon,
      email: detailedTransport.data?.client_details?.email,
      
      // Contact information
      nume_destinatar: detailedTransport.data?.raw_data?.nume_destinatar,
      telefon_destinatar: detailedTransport.data?.raw_data?.telefon_destinatar,
      telefon_expeditor: detailedTransport.data?.raw_data?.telefon_expeditor,
      
      // Financial details
      value: parseFloat(detailedTransport.data?.financial_details?.valoare_transport || 0),
      tip_plata: detailedTransport.data?.financial_details?.tip_plata,
      
      // Transport status and progress
      status: detailedTransport.data?.raw_data?.status,
      prioritate: detailedTransport.data?.raw_data?.prioritate || 'normala',
      progres: parseInt((detailedTransport.data?.raw_data?.progres?.toString().replace('%', '')) || '0', 10),
      
      // Route and location information
      route: detailedTransport.data?.route_details?.ruta_completa,
      distance: detailedTransport.data?.route_details?.distanta || cargoInfo.distance,
      startLocation: detailedTransport.data?.locations?.plecare?.oras || 
                    detailedTransport.data?.locations?.plecare?.adresa_completa?.split(',')[0],
      endLocation: detailedTransport.data?.locations?.destinatie?.oras ||
                  detailedTransport.data?.locations?.destinatie?.adresa_completa?.split(',')[0],
      plecare_adresa: detailedTransport.data?.locations?.plecare?.adresa_completa,
      destinatie_adresa: detailedTransport.data?.locations?.destinatie?.adresa_completa,
      client_adresa: detailedTransport.data?.client_details?.adresa,
      
      // Time information
      date: detailedTransport.data?.metadata?.created_at ? 
            new Date(detailedTransport.data.metadata.created_at).toISOString().split('T')[0] :
            new Date().toISOString().split('T')[0],
      ora_plecarii: detailedTransport.data?.raw_data?.ora_plecarii,
      ora_estimata: detailedTransport.data?.raw_data?.ora_estimata,
      
      // Cargo information
      weight: detailedTransport.data?.cargo_details?.greutate || cargoInfo.weight,
      cargoType: cargoInfo.cargoType,
      description: description,
      
      // Vehicle and driver information
      driver: detailedTransport.data?.vehicle_details?.sofer_nume,
      sofer_id: detailedTransport.data?.vehicle_details?.sofer_id,
      vehicle: detailedTransport.data?.vehicle_details?.vehicul_complet,
      vehicul_id: detailedTransport.data?.vehicle_details?.vehicul_id,
      
      // Location IDs for references
      locatie_plecare_id: detailedTransport.data?.locations?.plecare?.id,
      locatie_destinatie_id: detailedTransport.data?.locations?.destinatie?.id,
      
      // Timestamps
      created_at: detailedTransport.data?.metadata?.created_at,
      updated_at: detailedTransport.data?.metadata?.updated_at,
      
      // Vehicle location tracking data
      vehicul_adresa: detailedTransport.data?.vehicle_location?.adresa,
      vehicul_lat: detailedTransport.data?.vehicle_location?.lat,
      vehicul_lng: detailedTransport.data?.vehicle_location?.lng,
      vehicul_viteza: detailedTransport.data?.vehicle_location?.viteza,
      vehicul_ultima_pozitie: detailedTransport.data?.vehicle_location?.ultima_pozitie,
      
      // Tracking information
      tracking_token: detailedTransport.data?.raw_data?.tracking_token || 
                     detailedTransport.data?.transport?.tracking_token
    };
  } catch (error) {
    console.error('Eroare la încărcarea detaliilor:', error);
    throw error;
  }
}

  // Update transport
  async updateTransport(transportId, formData) {
    if (!formData.client || !formData.destinatie || !formData.ora_plecarii || !formData.value) {
      throw new Error('Vă rugăm completați toate câmpurile obligatorii!');
    }

    try {
      // Format datetime for API
      const formattedData = {
        ...formData,
        ora_plecarii: formData.ora_plecarii ? new Date(formData.ora_plecarii).toISOString() : null,
        ora_estimata: formData.ora_estimata ? new Date(formData.ora_estimata).toISOString() : null,
        value: parseFloat(formData.value) || 0,
        progres: parseInt(formData.progres) || 0
      };

      const response = await fetch(`${API_BASE_URL}/shipments/${transportId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Eroare la actualizare transport');
      }

      // Clear cache to force refresh
      localStorage.removeItem(STORAGE_KEY);
      
      return await response.json();
    } catch (error) {
      console.error('Eroare la actualizare:', error);
      throw error;
    }
  }

  // Create new transport
  async createTransport(formData) {
    if (!formData.client || !formData.destinatie || !formData.ora_plecarii || !formData.value) {
      throw new Error('Vă rugăm completați toate câmpurile obligatorii!');
    }

    try {
      // Format datetime for API
      const formattedData = {
        ...formData,
        ora_plecarii: formData.ora_plecarii ? new Date(formData.ora_plecarii).toISOString() : null,
        ora_estimata: formData.ora_estimata ? new Date(formData.ora_estimata).toISOString() : null,
        value: parseFloat(formData.value) || 0,
        progres: parseInt(formData.progres) || 0
      };

      const response = await fetch(`${API_BASE_URL}/shipments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Eroare la adăugare transport');
      }

      // Clear cache to force refresh
      localStorage.removeItem(STORAGE_KEY);
      
      return await response.json();
    } catch (error) {
      console.error('Eroare la adăugare transport:', error);
      throw error;
    }
  }

  // Delete transport
  async deleteTransport(transportId) {
    try {
      const response = await fetch(`${API_BASE_URL}/shipments/${transportId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Eroare la ștergerea transportului');
      }

      // Clear cache to force refresh
      localStorage.removeItem(STORAGE_KEY);
      
      return true;
    } catch (error) {
      console.error('Eroare la ștergere:', error);
      throw error;
    }
  }

  // Send location tracking link via email
  async sendLocationLink(transportId, clientId, method, data) {
    try {
      let endpoint;
      let payload;

      if (method === 'email') {
        // Folosim endpoint-ul existent pentru email
        endpoint = `${API_BASE_URL}/tracking/send-tracking-link`;
        payload = {
          clientId: clientId,
          transportId: transportId,
          ...data
        };
      } else {
        // Pentru alte metode (viitoare)
        endpoint = `${API_BASE_URL}/shipments/${transportId}/share-location`;
        payload = {
          client_id: clientId,
          method: method,
          ...data
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Eroare la trimiterea link-ului');
      }

      const result = await response.json();
      
      if (method === 'email' && !result.success) {
        throw new Error(result.message || 'Eroare la trimiterea email-ului');
      }

      return result;
    } catch (error) {
      console.error('Eroare la trimiterea link-ului:', error);
      throw error;
    }
  }

  // Generate tracking link from transport data
  generateTrackingLink(transport) {
    const baseUrl = window.location.origin;
    
    // Dacă transportul are tracking_token, folosește-l
    if (transport.tracking_token) {
      return `${baseUrl}/track/${transport.tracking_token}`;
    }
    
    // Fallback pentru transporturi mai vechi fără tracking_token
    // În acest caz, folosește ID-ul transportului (mai puțin sigur)
    return `${baseUrl}/track/${transport.id}`;
  }

  // Get tracking token from backend (pentru transporturi care nu au token)
  async getTrackingToken(transportId) {
    try {
      const response = await fetch(`${API_BASE_URL}/tracking/generate-link/${transportId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Nu s-a putut genera link-ul de urmărire');
      }

      const data = await response.json();
      return data.trackingUrl;
    } catch (error) {
      console.error('Eroare la generarea link-ului:', error);
      throw error;
    }
  }
}

export default TransportService;